import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

interface FieldMapping {
  salesLedger: string;
  shippingLedger: string;
  taxLedger: string;
  discountLedger: string;
  customerPrefix: string;
  productPrefix: string;
}

interface AppConfig {
  tallyVersion: 'erp9' | 'prime';
  defaultFormat: 'xml' | 'excel' | 'both';
  dateFormat: string;
  companyName: string;
  autoExport: boolean;
  fieldMapping: FieldMapping;
  filters: {
    includeShipping: boolean;
    includeTax: boolean;
    includeDiscounts: boolean;
    groupByDate: boolean;
  };
}

interface ConfigContextType {
  config: AppConfig | null;
  isLoading: boolean;
  error: string | null;
  updateConfig: (updates: Partial<AppConfig>) => Promise<void>;
  refreshConfig: () => Promise<void>;
}

const defaultConfig: AppConfig = {
  tallyVersion: 'prime',
  defaultFormat: 'xml',
  dateFormat: 'dd-MM-yyyy',
  companyName: 'My Shopify Store',
  autoExport: false,
  fieldMapping: {
    salesLedger: 'Sales',
    shippingLedger: 'Shipping Charges',
    taxLedger: 'CGST',
    discountLedger: 'Discount',
    customerPrefix: 'CUST-',
    productPrefix: 'PROD-'
  },
  filters: {
    includeShipping: true,
    includeTax: true,
    includeDiscounts: true,
    groupByDate: false
  }
};

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const ConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshConfig = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.config.getConfig();
      if (response.data.success) {
        setConfig(response.data.data.config);
      }
    } catch (err) {
      console.error('Failed to load config:', err);
      setConfig(defaultConfig);
    } finally {
      setIsLoading(false);
    }
  };

  const updateConfig = async (updates: Partial<AppConfig>) => {
    try {
      setIsLoading(true);
      const response = await api.config.updateConfig(updates);
      if (response.data.success) {
        setConfig(response.data.data.config);
      }
    } catch (err) {
      setError('Failed to update configuration');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshConfig();
  }, []);

  return (
    <ConfigContext.Provider value={{ config, isLoading, error, updateConfig, refreshConfig }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = (): ConfigContextType => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};
